import React, { FC, ReactNode } from "react";
import { useLazyQuery } from "@apollo/client";
import BloodDrawService from "@app/service/bloodDraw";

// Data-layer defaults for the lab locator. Live here because the provider
// owns `distance` state; the hook and UI consumers just read/import.
export const DEFAULT_DISTANCE_MI = 10;
export const DISTANCE_OPTIONS_MI = [10, 25, 50] as const;

export enum DAY_OF_WEEK {
  SUN = "Sun",
  MON = "Mon",
  TUE = "Tue",
  WED = "Wed",
  THU = "Thu",
  FRI = "Fri",
  SAT = "Sat",
}

export interface SiteHour {
  id: number;
  day_of_week: DAY_OF_WEEK;
  close?: string;
  open?: string;
  phlebotomy_site_id: number;
}

export interface BloodDrawSite {
  id: number;
  clinic_id: number;
  name: string;
  full_address: string;
  distance: number;
  phone: string;
  zip: string;
  site_hours: SiteHour[];
  comments?: string;
  appointment_scheduling_url?: string;
}

export type GetBloodDrawSitesResult =
  | { ok: true; sites: BloodDrawSite[] | undefined }
  | { ok: false };

type GetBloodDrawSites = (props: {
  location: string;
  distance?: number;
}) => Promise<GetBloodDrawSitesResult>;

export interface FetchedParams {
  location: string;
  distance: number;
}

export interface BloodDrawController {
  isLoading: boolean;
  bloodDrawSites?: BloodDrawSite[];
  getBloodDrawSites: GetBloodDrawSites;
  zipCode?: string;
  setZipCode: (zipCode: string) => void;
  distance: number;
  setDistance: (miles: number) => void;
  // The (location, distance) pair last successfully fetched. Consumers use
  // this to skip a redundant refetch when they mount with params that
  // already match what's in `bloodDrawSites`. Cleared on fetch failure so
  // the next mount retries.
  lastFetchedParams?: FetchedParams;
}

export const Context = React.createContext<BloodDrawController>({
  isLoading: false,
  bloodDrawSites: undefined,
  getBloodDrawSites: async () => ({ ok: true, sites: undefined }),
  zipCode: undefined,
  setZipCode: () => undefined,
  distance: DEFAULT_DISTANCE_MI,
  setDistance: () => undefined,
  lastFetchedParams: undefined,
});

Context.displayName = "BloodDrawContext";

const BloodDrawProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [bloodDrawSites, setBloodDrawSites] = React.useState<
    BloodDrawSite[] | undefined
  >();
  const [zipCode, setZipCode] = React.useState<string | undefined>();
  const [distance, setDistance] = React.useState<number>(DEFAULT_DISTANCE_MI);
  const [lastFetchedParams, setLastFetchedParams] = React.useState<
    FetchedParams | undefined
  >();

  const [getBloodDrawSites, { loading: getBloodDrawSitesIsLoading }] =
    useLazyQuery<{
      getBloodDrawSites: BloodDrawSite[];
    }>(BloodDrawService.getBloodDrawSites(), { fetchPolicy: "network-only" });

  const handleGetBloodDrawSites: GetBloodDrawSites = async (props) => {
    const response = await getBloodDrawSites({
      variables: { bloodDrawSitesData: props },
    });

    if (response.error) {
      // Clear any previously-shown sites so a stale list doesn't stay on
      // screen while the LabList surfaces the fetch-failed message. Also
      // clear lastFetchedParams so the next mount retries this pair.
      setBloodDrawSites(undefined);
      setLastFetchedParams(undefined);
      return { ok: false };
    }

    const sites = response.data?.getBloodDrawSites;
    setBloodDrawSites(sites);
    // props.distance is always set in practice (callers pass either the
    // hook's state or DEFAULT_DISTANCE_MI). If a caller ever omits it,
    // record 0 to guarantee a mismatch on the next explicit-distance mount
    // so we don't accidentally skip a real request.
    setLastFetchedParams({
      location: props.location,
      distance: props.distance ?? 0,
    });
    return { ok: true, sites };
  };

  const handleSetZipCode = (zipCode: string) => {
    setZipCode(zipCode);
  };

  return (
    <Context.Provider
      value={{
        isLoading: getBloodDrawSitesIsLoading,
        bloodDrawSites,
        getBloodDrawSites: handleGetBloodDrawSites,
        zipCode,
        setZipCode: handleSetZipCode,
        distance,
        setDistance,
        lastFetchedParams,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default BloodDrawProvider;
